import { ArrowLeft, Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import homeService, { HomePageData } from "../../services/HomeService";
import { useQuery } from "@tanstack/react-query";

const CarbFiberRatioDetail = () => {
  const navigate = useNavigate();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );

  // Use React Query to fetch and cache home page data
  const { data: homeData, isLoading: loading } = useQuery({
    queryKey: ["homePageData"],
    queryFn: () => homeService.getHomePageData(),
  });

  // Calendar functions
  const handleDateSelect = (dateString: string) => {
    const selectedDateObj = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDateObj.setHours(0, 0, 0, 0);

    if (selectedDateObj > today) {
      return;
    }

    setSelectedDate(dateString);
    setIsCalendarOpen(false);
    // TODO: Implement data fetching for selected date
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const navigateMonth = (direction: "prev" | "next") => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      if (direction === "prev") {
        newMonth.setMonth(prev.getMonth() - 1);
      } else {
        newMonth.setMonth(prev.getMonth() + 1);
      }
      return newMonth;
    });
  };

  // Get carb-fiber data from API
  const insightData = homeData?.["Health Insights"]?.carb_fiber_ratio_daily;
  const carbFiberRatio = insightData?.value ?? 0;
  const carbFiberStatus = insightData?.label ?? "Loading...";

  // Get actual gram values from Today's Nutrition
  const totalCarbs =
    homeData?.["Today's Nutrition"]?.["Total Carbohydrates"] || 0;
  const totalFiber = homeData?.["Today's Nutrition"]?.["Total Fiber"] || 0;

  // Calculate progress percentage (5:1 is excellent, 15:1 is poor)
  const getProgressPercentage = (ratio: number) => {
    // Inverse scale: lower ratio is better
    // 0 is best (100%), 25 is worst (0%)
    if (ratio <= 0) return 100;
    if (ratio >= 25) return 0;
    return ((25 - ratio) / 25) * 100;
  };

  // Get status color
  const getStatusColor = (status: string) => {
    if (status === "Excellent" || status === "Good") {
      return "text-green-600";
    }
    if (status === "Fair") {
      return "text-yellow-600";
    }
    if (status === "Needs Improvement" || status === "Poor") {
      return "text-red-600";
    }
    return "text-muted-foreground";
  };

  // Get progress bar color based on status
  const getProgressColor = (status: string) => {
    if (status === "Good" || status === "Excellent") {
      return "bg-green-500";
    }
    if (status === "Fair") {
      return "bg-yellow-500"; // Assuming yellow for Fair
    }
    if (status === "Needs Improvement" || status === "Poor") {
      return "bg-red-500";
    }
    return "bg-orange-500"; // Default
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-16">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/glucera" + GluceraNavigationRoutes.HOME)}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">
            Carb-to-Fiber Ratio
          </h1>
          <div className="h-10 w-10" />
        </div>

        {/* Today's Stats */}
        <Card className="from-warning/10 to-warning/5 rounded-2xl border-0 bg-gradient-to-br p-6 shadow-sm">
          <div className="text-center">
            <p className="mb-2 text-sm text-muted-foreground">
              Today's Carb-to-Fiber Ratio
            </p>
            <div className="mb-2 text-5xl font-bold text-foreground">
              {carbFiberRatio}
            </div>
            <p
              className={`text-sm font-semibold ${getStatusColor(carbFiberStatus)}`}
            >
              {carbFiberStatus} (Target: 5:1 or lower)
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Carbs: {Math.round(totalCarbs)}g • Fiber: {Math.round(totalFiber)}
              g
            </p>
            <div className="relative mt-4 h-3 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div
                className={`h-full rounded-full transition-all ${getProgressColor(carbFiberStatus)}`}
                style={{ width: `${getProgressPercentage(carbFiberRatio)}%` }}
              />
              {/* Status Markers */}
              <div className="absolute top-0 h-full w-px bg-white/50" style={{ left: `${100 - ((7 - 5) / 10) * 100}%` }} title="Excellent Limit (7)" />
              <div className="absolute top-0 h-full w-px bg-white/50" style={{ left: `${100 - ((12 - 5) / 10) * 100}%` }} title="Good Limit (12)" />
              <div className="absolute top-0 h-full w-px bg-white/50" style={{ left: `${100 - ((20 - 5) / 10) * 100}%` }} title="Moderate Limit (20)" />
            </div>
            <div className="mt-1 flex justify-between px-1 text-[10px] text-muted-foreground">
              <span>0</span>
              <span className="pl-4">7</span>
              <span className="pl-2">12</span>
              <span className="pl-1">20</span>
              <span>25+</span>
            </div>
          </div>
        </Card>

        {/* What is Carb-to-Fiber Ratio */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">
            What is Carb-to-Fiber Ratio?
          </h3>
          <p className="text-sm leading-relaxed text-muted-foreground">
            This ratio compares total carbohydrates to dietary fiber in your
            diet. A lower ratio indicates higher fiber content relative to
            carbs, which is beneficial for gut health, blood sugar control, and
            sustained energy. Whole foods naturally have better ratios, while
            processed foods tend to have high ratios.
          </p>
          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Excellent</span>
              <span className="text-success font-semibold">≤ 7</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Good</span>
              <span className="font-semibold">≤ 12</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Moderate</span>
              <span className="text-warning font-semibold">≤ 20</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Poor</span>
              <span className="font-semibold text-destructive">Above 20</span>
            </div>
          </div>
        </Card>

        {/* Improvement Tips */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">
            Ways to Improve Your Ratio
          </h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-warning mt-0.5">•</span>
              <span>
                Choose whole grains over refined grains (brown rice, quinoa,
                oats)
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-warning mt-0.5">•</span>
              <span>Add beans and lentils to your meals</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-warning mt-0.5">•</span>
              <span>Eat fruits with the skin on when possible</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-warning mt-0.5">•</span>
              <span>Snack on nuts, seeds, and high-fiber vegetables</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default CarbFiberRatioDetail;
